import { HttpClient, HttpEvent, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OperationResult } from '@tb/auth';
import { Observable, throwError } from 'rxjs';
import { UrlMaker } from 'src/app/commons/url-maker';
import { VerticalSolution } from 'src/app/model/vertical-solutions';
import { environment } from 'src/environments/environment';
import { LoginService } from './login.service';
import { SalesModule } from 'src/app/model/item';

@Injectable({
    providedIn: 'root',
})
export class VerticalSolutionsService {
    isModifyStatus: boolean = false;
    currentCodiceAzienda: string;
    resellerCompanyName: string;
    filesToCrypt: FileModel[] = [];

    constructor(private http: HttpClient, private loginService: LoginService) {}

    public async getVerticalSolution(language: string, accountName: string, name: string): Promise<OperationResult> {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: environment.auth.authToken });

        const url = new UrlMaker().appendBaseUrl(environment.settings.storeApiUrl).appendSegment('getVerticalSolution').toString();

        const jobj = {
            language: language,
            accountName: accountName,
            name: name,
        };
        return this.http.post<OperationResult>(url, JSON.stringify(jobj), { headers: headers }).toPromise();
    }

    public async saveOrUpdateVerticalSolution(
        verticalSolution: VerticalSolution,
        isNew: boolean,
        accountName: string
    ): Promise<OperationResult> {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: environment.auth.authToken });
        const url = new UrlMaker()
            .appendBaseUrl(environment.settings.storeApiUrl)
            .appendSegment('saveorupdateverticalsolution')
            .appendParameter('accountName', accountName)
            .appendParameter('isNew', isNew)
            .toString();
        return this.http.post<OperationResult>(url, JSON.stringify(verticalSolution), { headers: headers }).toPromise();
    }

    public async generateKeys(verticalSolution: VerticalSolution, accountName: string): Promise<OperationResult> {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: environment.auth.authToken });
        const url = new UrlMaker()
            .appendBaseUrl(environment.settings.storeApiUrl)
            .appendSegment('generateKeys')
            .appendParameter('accountName', accountName)
            .toString();
        return this.http.post<OperationResult>(url, JSON.stringify(verticalSolution), { headers: headers }).toPromise();
    }

    public async getKey(codiceAzienda: string, accountName: string): Promise<OperationResult> {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: environment.auth.authToken });
        const url = new UrlMaker()
            .appendBaseUrl(environment.settings.storeApiUrl)
            .appendSegment('getKey')
            .appendParameter('codiceAzienda', codiceAzienda)
            .appendParameter('accountName', accountName)
            .toString();
        return this.http.get<OperationResult>(url, { headers: headers }).toPromise();
    }

    public async getAllProductsByReseller(codiceAzienda: string, onlyActive: boolean, noEmbedded: boolean): Promise<OperationResult> {
        const url = new UrlMaker().appendBaseUrl(environment.settings.storeApiUrl).appendSegment('getAllProductsByReseller');

        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const jobj = {
            codiceAzienda: codiceAzienda,
            onlyActive: onlyActive,
            authToken: environment.auth.authToken,
            noEmbedded: noEmbedded,
        };
        return this.http
            .post<OperationResult>(url.toString(), JSON.stringify(jobj), {
                headers: headers,
            })
            .toPromise();
    }

    // --------------------------------------------------------------------------------
    public async CryptFiles(uploadedFiles: File[], accountName: string, signature: string, guid: string) {
        const url = new UrlMaker()
            .appendBaseUrl(environment.settings.microareaserviceproxy)
            //.appendBaseUrl(environment.settings.storeApiUrl)
            .appendSegment('cryptsalesmodules')
            .appendParameter('accountName', accountName)
            .appendParameter('signature', signature)
            .appendParameter('folderGuid', guid);
        const uploadData = new FormData();
        for (let i = 0; i < uploadedFiles.length; i++) {
            let file = uploadedFiles[i];
            uploadData.append('uploadedFiles', file, file.name);
        }

        return this.http.post<OperationResult>(url.toString(), uploadData).toPromise();
    }
    // --------------------------------------------------------------------------------
    public downloadFile(accountName: string, signature: string, guid: string): Observable<HttpEvent<Blob>> {
        let authorization = {
            type: 'jwt',
            appId: 'ST',
            securityValue: this.loginService.getAuthInfoProperties().jwtEncoded,
        };

        const headers = new HttpHeaders({ Authorization: JSON.stringify(authorization) });
        const url = new UrlMaker()
            .appendBaseUrl(environment.settings.microareaserviceproxy)
            .appendSegment('api')
            .appendSegment('download')
            .appendParameter('accountName', accountName)
            .appendParameter('signature', signature)
            .appendParameter('guid', guid);

        //return this.http.get(url.toString(), { observe: 'response', responseType: 'blob' });
        return this.http.request(
            new HttpRequest('GET', url.toString(), null, { reportProgress: true, responseType: 'blob', headers: headers })
        );
    }

    public deleteFile(accountName: string, signature: string, guid: string): Promise<OperationResult> {
        const url = new UrlMaker()
            .appendBaseUrl(environment.settings.microareaserviceproxy)
            .appendSegment('api')
            .appendSegment('delete')
            .appendParameter('accountName', accountName)
            .appendParameter('signature', signature)
            .appendParameter('guid', guid);

        return this.http.get<OperationResult>(url.toString()).toPromise();
    }

    public async getEmbeddedItemsCode(signature: string, accountName: string): Promise<OperationResult> {
        const url = new UrlMaker()
            .appendBaseUrl(environment.settings.storeApiUrl)
            .appendSegment('getEmbeddedItemsCode')
            .appendParameter('signature', signature)
            .appendParameter('accountName', accountName)
            .toString();

        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.get<OperationResult>(url, { headers: headers }).toPromise();
    }

    public async getSupportedCountries(): Promise<OperationResult> {
        const url = new UrlMaker()
            .appendBaseUrl(environment.settings.microareaserviceproxy)
            .appendSegment('api')
            .appendSegment('getsupportedcountries')
            .toString();

        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.get<OperationResult>(url, { headers: headers }).toPromise();
    }
    public async generateSerialNumbers(
        salesModulesList: SalesModule[],
        signature: string,
        codiceAzienda: string,
        country: string
    ): Promise<OperationResult> {
        const url = new UrlMaker()
            .appendBaseUrl(environment.settings.microareaserviceproxy)
            .appendSegment('api')
            .appendSegment('generateserialnumbers')
            .appendParameter('signature', signature)
            .appendParameter('codiceAzienda', codiceAzienda)
            .appendParameter('country', country)
            .toString();

        const jobj = {
            salesModulesList: salesModulesList,
            authToken: environment.auth.authToken,
        };
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http
            .post<OperationResult>(url.toString(), JSON.stringify(jobj), {
                headers: headers,
            })
            .toPromise();
    }
}
export class FileModel {
    FileToCrypt: File;
    FileName: string;
    Description: string;
}
